import { getWillieSupabaseClient } from '../../utils'
import { WaterStationSchema } from '../stations/schemas'
import { TablesInsert } from '@willie/supabase'
import { CreateSiteSchema, SiteSchema } from './schemas'
import Sentry from '~/tools/sentry'

export const createSite = async (data: CreateSiteSchema): Promise<void> => {
  const supabase = getWillieSupabaseClient()
  const payload: TablesInsert<'sites'> = {
    name: data.name,
    description: data.description,
    address: data.address,
    internal_id: data.internalID || null,
    org_id: data.orgID,
    coordinates: data.coordinates || null
  }
  await supabase.from('sites').insert(payload)
}

export const updateSite = async (data: SiteSchema): Promise<void> => {
  const supabase = getWillieSupabaseClient()
  const payload: TablesInsert<'sites'> = {
    id: data.id,
    name: data.name,
    description: data.description,
    address: data.address,
    org_id: data.orgID,
    internal_id: data.internalID || null,
    coordinates: data.coordinates || null,
    droughts_incidents_notifications_enabled: data.droughtIncidentEnabled
  }
  await supabase.from('sites').update(payload).eq('id', data.id)
}

export const getSite = async ({ siteId }: { siteId: string }) => {
  const supabase = getWillieSupabaseClient()
  const { data, error } = await supabase
    .from('sites')
    .select(
      `*,
      stations:water_stations (*)`
    )
    .eq('id', siteId)
    .single()
  if (error || !data) {
    console.log('Error fetching site', error)
    throw new Error(`Site with id ${siteId} not found`)
  }

  const parsedStations = data.stations
    .map((station) => {
      try {
        return WaterStationSchema.parse({
          ...station,
          cost: station.cost_per_m3,
          leakTriggerConfig: station.leak_trigger_configs,
          internalID: station.internal_id
        })
      } catch (error) {
        console.error('Error parsing station', station.id, error)
        return null
      }
    })
    .filter((station) => !!station)

  return {
    ...data,
    orgID: data.org_id,
    internalID: data.internal_id,
    stations: parsedStations
  }
}

export type SiteWithStations = Awaited<ReturnType<typeof getSite>>
export type SiteSummary = Awaited<ReturnType<typeof listSites>>[number]

export const listSites = async ({ orgId }: { orgId: string }) => {
  const supabase = getWillieSupabaseClient()
  const { data, error } = await supabase
    .from('sites')
    .select(
      `*,
      stations:water_stations (*)
    `
    )
    .eq('org_id', orgId)
  if (error) {
    console.error('Error fetching sites', error)
  }
  return data || []
}

export const deleteSite = async (siteId: string): Promise<void> => {
  const supabase = getWillieSupabaseClient()
  await supabase.from('sites').delete().eq('id', siteId)
}

export interface LocationSuggestionFeature {
  type: string
  properties: {
    label: string
    id: string
    postcode: string
    city: string
    district: string
    street: string
    housenumber: string
    citycode: string
    x: number
    y: number
    score: number
    _score: number
    name: string
    type: string
    _type: string
    contexte: string
    importance: string
  }
  geometry: {
    type: string
    coordinates: [number, number]
  }
}

/**
 *
 * @param placeQuery The query string to search for location suggestions.
 * @note This is only valid for France, as it uses the geocoding service from geopf.fr . When we need a more global solution, let's use Google places api ==> has the best results after half a day of testing other services.
 */
export const fetchLocationSuggestions = async (placeQuery: string): Promise<LocationSuggestionFeature[]> => {
  if (!placeQuery || placeQuery.length < 3) {
    return []
  }
  const encoded = encodeURIComponent(placeQuery)
  try {
    const response = await fetch(`https://data.geopf.fr/geocodage/search?q=${encoded}`)
    const data = await response.json()
    if (!data || !data.features) {
      return []
    }
    return data.features as LocationSuggestionFeature[]
  } catch (error) {
    console.error('Error fetching site location suggestions', error)
    Sentry.captureException(error)
    return []
  }
}
